import { useMemo, useState } from 'react';

import {
  useGetAccounts,
  useGetApplication,
  useGetIwocapayPaymentCaptured,
  useGetIwocapayPaymentLater,
  useGetStateByStateKey,
} from '../../../api/lending/lapiHooks';
import {
  getApplicationState,
  getCancelledPlans,
  getCapturedPlans,
  getCreditLimit,
  getEndedPayments,
  getIwocaPayApplication,
  getOngoingPaymentPlans,
  getPaymentPlans,
  getTotalScheduledPayments,
} from '../Dashboard.helpers';
import { Plan } from '../Dashboard.types';
import { useApplicationProcessor } from './useApplicationProcessor';

export function useAccount() {
  const { state, loadingState } = useGetStateByStateKey();
  const { application: rawApplication, loadingApplication } =
    useGetApplication();
  const { accounts, loadingAccounts } = useGetAccounts();
  const { buyerPayLinks, loadingBuyerPayLinks } = useGetIwocapayPaymentLater();
  const { capturedPayments, loadingCapturedPayments } =
    useGetIwocapayPaymentCaptured();

  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  const iwocaPayApplication = useMemo(
    () => (rawApplication ? getIwocaPayApplication(rawApplication) : null),
    [rawApplication],
  );

  // Process open applications
  const shouldProcess =
    rawApplication?.status === 'open' &&
    rawApplication?.requested_products?.some(
      (p) => p.product_type === 'iwocapay',
    );

  useApplicationProcessor({
    applicationStatus: shouldProcess ? rawApplication?.status : undefined,
  });

  const isLoading =
    loadingState ||
    loadingApplication ||
    loadingAccounts ||
    loadingBuyerPayLinks ||
    loadingCapturedPayments;

  const hasAllData =
    !!state && !!rawApplication && !!accounts && !!buyerPayLinks;

  const ready = !isLoading && hasAllData;

  const error =
    !isLoading && !hasAllData
      ? new Error('Failed to load dashboard data')
      : null;

  const dashboardData = useMemo(() => {
    if (!ready || !accounts?.accounts || !buyerPayLinks || !rawApplication) {
      return {
        application: null,
        ongoingPaymentPlans: [],
        capturedPlans: [],
        cancelledPayments: [],
        endedPayments: [],
        creditLimit: null,
        plans: [] as Plan[],
      };
    }

    const payLinks = buyerPayLinks;
    const capturedPaymentsData = capturedPayments || [];

    const totalScheduledAmount = getTotalScheduledPayments(
      payLinks,
      capturedPaymentsData,
    );

    const capturedPlansList = getCapturedPlans(capturedPaymentsData, payLinks);
    const paymentPlans = getPaymentPlans(accounts.accounts, payLinks);
    const applicationState = getApplicationState({
      accounts: accounts.accounts,
      payLinks,
      application: rawApplication,
      totalScheduledAmount,
      capturedPayments: capturedPaymentsData,
    });

    const ongoingPaymentPlans = getOngoingPaymentPlans(paymentPlans);
    const cancelledPayments = getCancelledPlans(capturedPaymentsData, payLinks);
    const endedPayments = getEndedPayments(accounts.accounts, payLinks);
    const creditLimit = getCreditLimit(accounts.accounts, totalScheduledAmount);

    const plans: Plan[] = [
      ...paymentPlans,
      ...capturedPlansList,
      ...cancelledPayments,
      applicationState,
    ];

    return {
      application: applicationState,
      ongoingPaymentPlans,
      capturedPlans: capturedPlansList,
      cancelledPayments,
      endedPayments,
      creditLimit,
      plans,
    };
  }, [ready, accounts, buyerPayLinks, rawApplication, capturedPayments]);

  // Set the initial selected plan when data becomes available
  useMemo(() => {
    if (!ready) return;
    if (selectedPlan !== null) return;

    const initialPlan =
      dashboardData.ongoingPaymentPlans[0] ||
      dashboardData.application ||
      dashboardData.capturedPlans[0] ||
      null;

    if (initialPlan) {
      setSelectedPlan(initialPlan);
    }
  }, [ready, dashboardData, selectedPlan]);

  const buyerFirstName = state?.application?.people?.[0]?.first_name || '';
  const isSoleTrader = state?.application?.company?.type === 'sole_trader';

  return {
    ready,
    error,
    buyerFirstName,
    application: dashboardData.application,
    selectedPlan,
    setSelectedPlan,
    ongoingPaymentPlans: dashboardData.ongoingPaymentPlans,
    capturedPlans: dashboardData.capturedPlans,
    cancelledPayments: dashboardData.cancelledPayments,
    endedPayments: dashboardData.endedPayments,
    iwocaPayApplication,
    rawApplication,
    creditLimit: dashboardData.creditLimit,
    isSoleTrader,
    plans: dashboardData.plans,
  };
}
